/**
 * WhatsAppTab — conversation thread (Z-API inbound replies + outbound) shown
 * inside the lead drawer, with a quick reply box.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WhatsAppTab({ lead }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = () => {
    axios.get(`${API}/leads/${lead.id}/wa/conversation`)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [lead.id]); // eslint-disable-line
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [items.length]);

  const reply = async () => {
    const m = msg.trim();
    if (!m) return;
    setSending(true);
    try {
      await axios.post(`${API}/leads/${lead.id}/wa/reply`, { message: m });
      setMsg("");
      load();
      toast.success("Enviado via WhatsApp");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no envio");
    } finally { setSending(false); }
  };

  return (
    <div className="flex flex-col h-full" data-testid="ldd-wa-tab">
      {loading ? (
        <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-[var(--al-text-muted)]">
          <MessageCircle className="w-6 h-6 mx-auto mb-2 opacity-50" />
          <p className="text-[12px]">Nenhuma conversa ainda. Quando o lead responder no WhatsApp, as mensagens aparecem aqui e o card vira <b>QUENTE</b> 🔥</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1" data-testid="ldd-wa-thread">
          {items.map((m) => (
            <div key={m.id} className={`max-w-[85%] rounded-xl px-3 py-2 text-[12px] leading-snug ${
              m.direction === "inbound"
                ? "bg-[var(--al-bg)] border border-[var(--al-border)]"
                : "bg-[#DCF8C6] text-[#0b3d2e] ml-auto"}`}>
              {m.message}
              <div className="text-[9.5px] opacity-60 mt-0.5">
                {m.direction === "inbound" ? (m.sender_name || "Lead") : "Você"} · {new Date(m.at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
      )}
      <div className="flex gap-2 mt-3">
        <input value={msg} onChange={(e) => setMsg(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") reply(); }}
          placeholder="Responder no WhatsApp..."
          data-testid="ldd-wa-input"
          className="flex-1 h-9 px-3 rounded-lg border border-[var(--al-border)] bg-[var(--al-bg)] text-[12px] outline-none focus:border-[#128C7E]" />
        <button type="button" onClick={reply} disabled={sending || !msg.trim()}
          data-testid="ldd-wa-send"
          className="h-9 w-9 rounded-lg bg-[#128C7E] hover:bg-[#0e7266] text-white inline-flex items-center justify-center disabled:opacity-50">
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}
